import React, { useState, useEffect, useMemo } from 'react';
import { useWorkbench } from '../WorkbenchContext';
import MarkdownViewer from './MarkdownViewer';

const TEMPLATE_TYPES = [
  // Contracts & Agreements
  { id: 'nda', name: 'Non-Disclosure Agreement', icon: '🔒', description: 'Confidentiality agreement template' },
  { id: 'service_agreement', name: 'Service Agreement', icon: '📋', description: 'Professional services contract template' },
  { id: 'employment_contract', name: 'Employment Contract', icon: '👔', description: 'Employment agreement template' },
  { id: 'consulting_agreement', name: 'Consulting Agreement', icon: '💼', description: 'Independent contractor agreement' },
  { id: 'purchase_agreement', name: 'Purchase Agreement', icon: '🛒', description: 'Asset or business purchase contract' },
  { id: 'lease_agreement', name: 'Lease Agreement', icon: '🏢', description: 'Commercial or residential lease' },

  // Litigation Documents
  { id: 'complaint', name: 'Complaint', icon: '⚖️', description: 'Initial pleading to commence lawsuit' },
  { id: 'answer', name: 'Answer', icon: '📝', description: 'Response to complaint with defenses' },
  { id: 'motion_dismiss', name: 'Motion to Dismiss', icon: '🚫', description: 'Motion to dismiss claims' },
  { id: 'motion_summary', name: 'Motion for Summary Judgment', icon: '⚡', description: 'Motion for judgment as matter of law' },
  { id: 'brief', name: 'Legal Brief', icon: '📄', description: 'Memorandum of law in support of motion' },

  // Discovery Documents
  { id: 'interrogatories', name: 'Interrogatories', icon: '❓', description: 'Written questions to opposing party' },
  { id: 'rfp', name: 'Request for Production', icon: '📦', description: 'Document production requests' },
  { id: 'rfa', name: 'Request for Admissions', icon: '✓', description: 'Requests to admit facts' },
  { id: 'deposition_notice', name: 'Deposition Notice', icon: '🎤', description: 'Notice of deposition' },

  // Corporate Documents
  { id: 'operating_agreement', name: 'Operating Agreement', icon: '🏛️', description: 'LLC operating agreement' },
  { id: 'bylaws', name: 'Corporate Bylaws', icon: '📜', description: 'Corporate governance rules' },
  { id: 'board_resolution', name: 'Board Resolution', icon: '✍️', description: 'Board of directors resolution' },
  { id: 'shareholder_agreement', name: 'Shareholder Agreement', icon: '🤝', description: 'Rights and obligations of shareholders' },

  // Legal Memos & Analysis
  { id: 'legal_memo', name: 'Legal Memorandum', icon: '📋', description: 'Legal analysis and research memo' },
  { id: 'opinion_letter', name: 'Opinion Letter', icon: '💌', description: 'Legal opinion on specific matter' },
  { id: 'demand_letter', name: 'Demand Letter', icon: '📮', description: 'Formal demand for action or payment' },

  // IP Documents
  { id: 'patent_application', name: 'Patent Application', icon: '💡', description: 'USPTO patent application' },
  { id: 'trademark_application', name: 'Trademark Application', icon: '™️', description: 'USPTO trademark application' },
  { id: 'licensing_agreement', name: 'Licensing Agreement', icon: '🎫', description: 'IP licensing contract' }
];

function PackageDesign() {
  const {
    activeCase,
    cases,
    addArtifactToCase,
    setActiveTab,
    setSelectedReferenceCase,
    setActiveCaseId
  } = useWorkbench();

  // State for case and template selection
  const [selectedCaseId, setSelectedCaseId] = useState(activeCase?.id || '');
  const [selectedTemplateTypes, setSelectedTemplateTypes] = useState(new Set());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [generatedTemplates, setGeneratedTemplates] = useState([]);

  // Update selectedCaseId when activeCase changes (e.g., coming from My Cases)
  useEffect(() => {
    if (activeCase?.id && !selectedCaseId) {
      setSelectedCaseId(activeCase.id);
    }
  }, [activeCase?.id]);

  // Get the currently selected case
  const selectedCase = useMemo(() => {
    return cases.find(c => c.id === selectedCaseId) || null;
  }, [cases, selectedCaseId]);

  // Combine documents and artifacts into one list for input selection
  // De-duplicate by title to avoid showing same document twice
  const allCaseDocuments = useMemo(() => {
    if (!selectedCase) return [];

    const docs = [];
    const seenTitles = new Set();

    // Add uploaded documents first (they take priority)
    if (selectedCase.documents && selectedCase.documents.length > 0) {
      selectedCase.documents.forEach(doc => {
        const title = doc.title || doc.filename;
        if (!seenTitles.has(title)) {
          seenTitles.add(title);
          docs.push({
            id: doc.id || `doc_${doc.filename}`,
            title: title,
            type: 'uploaded',
            content: doc.content || '',
            source: doc
          });
        }
      });
    }

    // Add created artifacts (skip if title already exists from documents)
    if (selectedCase.artifacts && selectedCase.artifacts.length > 0) {
      selectedCase.artifacts.forEach(artifact => {
        if (!seenTitles.has(artifact.title)) {
          seenTitles.add(artifact.title);
          docs.push({
            id: artifact.id || `artifact_${artifact.title}`,
            title: artifact.title,
            type: artifact.type || 'artifact',
            content: artifact.content || '',
            source: artifact
          });
        }
      });
    }

    return docs;
  }, [selectedCase]);

  // Clear document selection when case changes
  useEffect(() => {
    setSelectedDocumentIds(new Set());
  }, [selectedCaseId]);

  // Handle case selection change
  const handleCaseChange = (e) => {
    const newCaseId = e.target.value;
    setSelectedCaseId(newCaseId);
    // Also update the active case in context
    if (newCaseId) {
      setActiveCaseId(newCaseId);
    }
  };

  // Handle clicking on a case citation - navigate to Reference Library
  const handleCaseClick = (caseId) => {
    setSelectedReferenceCase(caseId);
    setActiveTab('references');
  };

  // Toggle template type selection
  const handleTemplateToggle = (templateId) => {
    setSelectedTemplateTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  // Toggle document selection
  const handleDocumentToggle = (docId) => {
    setSelectedDocumentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Select/deselect all documents
  const handleSelectAllDocuments = () => {
    if (selectedDocumentIds.size === allCaseDocuments.length) {
      setSelectedDocumentIds(new Set());
    } else {
      setSelectedDocumentIds(new Set(allCaseDocuments.map(d => d.id)));
    }
  };

  // Generate templates
  const handleGenerate = async () => {
    if (!selectedCase || selectedTemplateTypes.size === 0) return;

    const templatesToGenerate = TEMPLATE_TYPES.filter(t => selectedTemplateTypes.has(t.id));
    const selectedDocs = allCaseDocuments.filter(d => selectedDocumentIds.has(d.id));

    setIsGenerating(true);
    setStreamingContent('');
    setGeneratedTemplates([]);

    try {
      // Build case context with selected documents (both uploaded and artifacts)
      const caseContext = {
        caseName: selectedCase.name,
        documents: selectedDocs.map(d => ({
          id: d.id,
          title: d.title,
          type: d.type,
          content: d.content
        })),
        linkedReferences: selectedCase.linkedReferences || []
      };

      const generated = [];

      for (let i = 0; i < templatesToGenerate.length; i++) {
        const templateType = templatesToGenerate[i];
        const progress = `Generating ${i + 1} of ${templatesToGenerate.length}: ${templateType.name}...`;

        setStreamingContent(progress);

        const response = await fetch('/api/package-design/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            documentType: templateType,
            caseContext: caseContext,
            artifacts: [], // Documents are now in caseContext
            conversationHistory: []
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error for ${templateType.name}: ${response.status}`);
        }

        // Stream the response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let templateContent = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6).trim();
                if (!jsonStr) continue;

                const data = JSON.parse(jsonStr);
                if (data.type === 'text') {
                  templateContent += data.content;
                  setStreamingContent(`${progress}\n\n${templateContent}`);
                } else if (data.type === 'result') {
                  templateContent = data.content;
                } else if (data.type === 'error') {
                  throw new Error(data.content || 'Generation failed');
                }
              } catch (e) {
                if (!e.message.includes('Unexpected end of JSON') &&
                    !e.message.includes('Unterminated string')) {
                  console.error('Parse error:', e);
                }
              }
            }
          }
        }

        // Create artifact and add to case
        const artifact = {
          id: `template_${Date.now()}_${i}`,
          title: `${templateType.name} - ${new Date().toLocaleDateString()}`,
          content: templateContent,
          type: 'template',
          templateType: templateType.id,
          templateName: templateType.name,
          createdAt: new Date().toISOString()
        };

        addArtifactToCase(selectedCase.id, artifact);
        generated.push({ ...artifact, templateTypeInfo: templateType });
      }

      setGeneratedTemplates(generated);
      setStreamingContent('');

    } catch (error) {
      console.error('Generation error:', error);
      setStreamingContent(`Error: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset and create more
  const handleCreateMore = () => {
    setGeneratedTemplates([]);
    setSelectedTemplateTypes(new Set());
    setStreamingContent('');
  };

  // If no cases exist at all
  if (cases.length === 0) {
    return (
      <div className="template-creation-container">
        <div className="no-case-prompt">
          <div className="prompt-icon">&#9878;</div>
          <h2>No Cases Available</h2>
          <p>
            Create a case first to start generating templates. Your case documents
            will be used as input for pre-filling template information.
          </p>
          <button
            onClick={() => setActiveTab('my-cases')}
            className="btn-select-case"
          >
            Go to My Cases
          </button>
        </div>

        <style>{`
          .template-creation-container {
            height: 100%;
            display: flex;
            flex-direction: column;
            background: rgba(15, 13, 38, 0.95);
          }

          .no-case-prompt {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            text-align: center;
            padding: 3rem;
          }

          .prompt-icon {
            font-size: 5rem;
            margin-bottom: 1.5rem;
          }

          .no-case-prompt h2 {
            color: #e0e9ff;
            font-size: 1.75rem;
            margin-bottom: 1rem;
          }

          .no-case-prompt p {
            color: rgba(224, 233, 255, 0.7);
            max-width: 500px;
            margin-bottom: 2rem;
            line-height: 1.6;
          }

          .btn-select-case {
            padding: 0.75rem 2rem;
            background: rgba(99, 102, 241, 0.8);
            border: 1px solid rgba(99, 102, 241, 0.9);
            border-radius: 8px;
            color: #fff;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .btn-select-case:hover {
            background: rgba(99, 102, 241, 1);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="template-creation-container">
      {/* Case Selection Header */}
      <div className="case-selection-header">
        <div className="case-selector">
          <label htmlFor="case-select">&#9878; Select Case:</label>
          <select
            id="case-select"
            value={selectedCaseId}
            onChange={handleCaseChange}
            className="case-dropdown"
          >
            <option value="">-- Select a case --</option>
            {cases.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {selectedCase && (
          <div className="case-stats">
            <span>{selectedCase.documents?.length || 0} uploaded</span>
            <span>&#x2022;</span>
            <span>{selectedCase.artifacts?.length || 0} created</span>
            <span>&#x2022;</span>
            <span><strong>{allCaseDocuments.length}</strong> total documents</span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="template-body">
        {/* Left Panel - Input Documents */}
        {!isGenerating && generatedTemplates.length === 0 && (
          <div className="input-panel">
            <div className="panel-header">
              <h3>&#128196; Input Documents</h3>
              <p>Select documents to use as context</p>
            </div>

            {!selectedCase ? (
              <div className="no-documents">
                <p>Select a case above to see its documents.</p>
              </div>
            ) : allCaseDocuments.length > 0 ? (
              <>
                <div className="document-controls">
                  <button onClick={handleSelectAllDocuments} className="btn-select-all">
                    {selectedDocumentIds.size === allCaseDocuments.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="selection-count">
                    {selectedDocumentIds.size} / {allCaseDocuments.length}
                  </span>
                </div>

                <div className="document-list">
                  {allCaseDocuments.map(doc => (
                    <label key={doc.id} className={`document-item ${selectedDocumentIds.has(doc.id) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedDocumentIds.has(doc.id)}
                        onChange={() => handleDocumentToggle(doc.id)}
                      />
                      <div className="doc-info">
                        <span className="doc-title">{doc.title}</span>
                        <span className={`doc-type ${doc.type}`}>
                          {doc.type === 'uploaded' ? '📤 Uploaded' :
                           doc.type === 'template' ? '📝 Template' :
                           '📄 Created'}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-documents">
                <p>No documents in this case yet.</p>
                <p className="hint">Upload documents or create content in Document Analysis.</p>
              </div>
            )}
          </div>
        )}

        {/* Main Workspace */}
        <div className="workspace">
          {!selectedCase ? (
            <div className="select-case-prompt">
              <h2>&#9878;&#65039; Select a Case</h2>
              <p>Choose a case from the dropdown above to start creating templates.</p>
            </div>
          ) : !isGenerating && generatedTemplates.length === 0 ? (
            // Template Type Selection
            <div className="template-selection">
              <div className="selection-header">
                <h2>&#9878;&#65039; Select Templates to Generate</h2>
                <p>Choose one or more template types. Selected input documents will be used to pre-fill available information.</p>
              </div>

              <div className="template-grid">
                {TEMPLATE_TYPES.map(template => (
                  <label
                    key={template.id}
                    className={`template-card ${selectedTemplateTypes.has(template.id) ? 'selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTemplateTypes.has(template.id)}
                      onChange={() => handleTemplateToggle(template.id)}
                    />
                    <div className="template-icon">{template.icon}</div>
                    <div className="template-name">{template.name}</div>
                    <div className="template-description">{template.description}</div>
                  </label>
                ))}
              </div>

              <div className="generation-controls">
                <div className="selection-summary">
                  <strong>{selectedTemplateTypes.size}</strong> template{selectedTemplateTypes.size !== 1 ? 's' : ''} selected
                  {selectedDocumentIds.size > 0 && (
                    <span className="input-summary">
                      {' '}&#x2022; <strong>{selectedDocumentIds.size}</strong> input document{selectedDocumentIds.size !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={selectedTemplateTypes.size === 0}
                  className="btn-generate"
                >
                  &#128221; Generate {selectedTemplateTypes.size > 1 ? 'Templates' : 'Template'}
                </button>
              </div>
            </div>
          ) : isGenerating ? (
            // Generating state
            <div className="generation-progress">
              <div className="progress-header">
                <div className="spinner"></div>
                <span>Generating templates...</span>
              </div>
              <div className="streaming-preview">
                <MarkdownViewer content={streamingContent || 'Starting generation...'} onCaseClick={handleCaseClick} />
              </div>
            </div>
          ) : generatedTemplates.length > 0 ? (
            // Generated templates view
            <div className="templates-result">
              <div className="result-header">
                <div className="result-info">
                  <h3>&#10003; {generatedTemplates.length} Template{generatedTemplates.length !== 1 ? 's' : ''} Created</h3>
                  <p>Templates have been saved to case: {selectedCase.name}</p>
                </div>
                <div className="result-actions">
                  <button onClick={() => setActiveTab('requirements')} className="btn-open-analysis">
                    &#128196; View in Document Analysis
                  </button>
                  <button onClick={handleCreateMore} className="btn-create-more">
                    + Create More
                  </button>
                </div>
              </div>

              <div className="generated-list">
                {generatedTemplates.map((template) => (
                  <div key={template.id} className="generated-item">
                    <div className="item-header">
                      <span className="item-icon">{template.templateTypeInfo.icon}</span>
                      <span className="item-title">{template.title}</span>
                    </div>
                    <div className="item-preview">
                      <MarkdownViewer content={template.content} onCaseClick={handleCaseClick} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <style>{`
        .template-creation-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: rgba(15, 13, 38, 0.95);
        }

        .case-selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: rgba(99, 102, 241, 0.15);
          border-bottom: 1px solid rgba(99, 102, 241, 0.3);
        }

        .case-selector {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .case-selector label {
          color: #e0e9ff;
          font-weight: 600;
          font-size: 1rem;
        }

        .case-dropdown {
          padding: 0.5rem 1rem;
          background: rgba(30, 27, 75, 0.8);
          border: 1px solid rgba(99, 102, 241, 0.4);
          border-radius: 6px;
          color: #e0e9ff;
          font-size: 0.95rem;
          min-width: 250px;
          cursor: pointer;
        }

        .case-dropdown:hover {
          border-color: rgba(99, 102, 241, 0.6);
        }

        .case-dropdown:focus {
          outline: none;
          border-color: rgba(99, 102, 241, 0.8);
        }

        .case-dropdown option {
          background: rgba(30, 27, 75, 1);
          color: #e0e9ff;
        }

        .case-stats {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: rgba(224, 233, 255, 0.6);
        }

        .template-body {
          flex: 1;
          display: flex;
          overflow: hidden;
        }

        /* Input Panel */
        .input-panel {
          width: 300px;
          background: rgba(20, 17, 48, 0.9);
          border-right: 1px solid rgba(99, 102, 241, 0.2);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .panel-header {
          padding: 1rem;
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
        }

        .panel-header h3 {
          margin: 0 0 0.25rem 0;
          color: #e0e9ff;
          font-size: 1rem;
        }

        .panel-header p {
          margin: 0;
          color: rgba(224, 233, 255, 0.6);
          font-size: 0.8rem;
        }

        .document-controls {
          padding: 0.75rem 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(99, 102, 241, 0.1);
        }

        .btn-select-all {
          padding: 0.25rem 0.5rem;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: #e0e9ff;
          font-size: 0.75rem;
          cursor: pointer;
        }

        .btn-select-all:hover {
          background: rgba(99, 102, 241, 0.3);
        }

        .selection-count {
          font-size: 0.75rem;
          color: rgba(224, 233, 255, 0.5);
        }

        .document-list {
          flex: 1;
          overflow-y: auto;
          padding: 0.5rem;
        }

        .document-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
          background: rgba(99, 102, 241, 0.05);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .document-item:hover {
          background: rgba(99, 102, 241, 0.1);
        }

        .document-item.selected {
          background: rgba(99, 102, 241, 0.15);
          border-color: rgba(99, 102, 241, 0.4);
        }

        .document-item input {
          cursor: pointer;
        }

        .doc-info {
          flex: 1;
          min-width: 0;
        }

        .doc-title {
          display: block;
          color: #e0e9ff;
          font-size: 0.85rem;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .doc-type {
          display: inline-block;
          color: rgba(224, 233, 255, 0.6);
          font-size: 0.7rem;
          margin-top: 0.25rem;
          padding: 0.125rem 0.375rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 3px;
        }

        .doc-type.uploaded {
          color: rgba(147, 197, 253, 0.9);
        }

        .doc-type.template {
          color: rgba(167, 139, 250, 0.9);
        }

        .no-documents {
          padding: 2rem 1rem;
          text-align: center;
          color: rgba(224, 233, 255, 0.5);
        }

        .no-documents p {
          margin: 0 0 0.5rem 0;
        }

        .no-documents .hint {
          font-size: 0.8rem;
          color: rgba(224, 233, 255, 0.4);
        }

        /* Workspace */
        .workspace {
          flex: 1;
          overflow: hidden;
          padding: 2rem;
          display: flex;
          flex-direction: column;
        }

        .select-case-prompt {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1;
          text-align: center;
        }

        .select-case-prompt h2 {
          color: #e0e9ff;
          margin-bottom: 0.5rem;
        }

        .select-case-prompt p {
          color: rgba(224, 233, 255, 0.6);
        }

        .template-selection {
          width: 100%;
          flex: 1;
          overflow-y: auto;
        }

        .selection-header {
          margin-bottom: 1.5rem;
        }

        .selection-header h2 {
          margin: 0 0 0.5rem 0;
          color: #e0e9ff;
          font-size: 1.5rem;
        }

        .selection-header p {
          margin: 0;
          color: rgba(224, 233, 255, 0.7);
          font-size: 0.9rem;
        }

        .template-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .template-card {
          background: rgba(30, 27, 75, 0.6);
          border: 2px solid rgba(99, 102, 241, 0.3);
          border-radius: 8px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
        }

        .template-card input[type="checkbox"] {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .template-card:hover {
          border-color: rgba(99, 102, 241, 0.6);
          background: rgba(30, 27, 75, 0.8);
        }

        .template-card.selected {
          border-color: rgba(99, 102, 241, 0.8);
          background: rgba(99, 102, 241, 0.2);
        }

        .template-icon {
          font-size: 2rem;
          margin-bottom: 0.5rem;
        }

        .template-name {
          color: #e0e9ff;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .template-description {
          color: rgba(224, 233, 255, 0.6);
          font-size: 0.75rem;
          line-height: 1.4;
        }

        .generation-controls {
          background: rgba(30, 27, 75, 0.6);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 8px;
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .selection-summary {
          color: #e0e9ff;
          font-size: 1rem;
        }

        .input-summary {
          color: rgba(224, 233, 255, 0.7);
        }

        .btn-generate {
          padding: 0.75rem 2rem;
          background: rgba(99, 102, 241, 0.8);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-generate:hover:not(:disabled) {
          background: rgba(99, 102, 241, 1);
        }

        .btn-generate:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Generation Progress */
        .generation-progress {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
        }

        .progress-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: rgba(99, 102, 241, 0.1);
          border-radius: 8px;
          margin-bottom: 1rem;
          color: #e0e9ff;
        }

        .spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(99, 102, 241, 0.3);
          border-top-color: rgba(99, 102, 241, 0.8);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .streaming-preview {
          flex: 1;
          overflow-y: auto;
          background: rgba(20, 17, 48, 0.6);
          border-radius: 8px;
          padding: 1rem;
          min-height: 0;
        }

        /* Templates Result */
        .templates-result {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: rgba(34, 197, 94, 0.15);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .result-info h3 {
          margin: 0;
          color: #e0e9ff;
          font-size: 1.1rem;
        }

        .result-info p {
          margin: 0.25rem 0 0 0;
          color: rgba(34, 197, 94, 0.9);
          font-size: 0.85rem;
        }

        .result-actions {
          display: flex;
          gap: 0.75rem;
        }

        .btn-open-analysis, .btn-create-more {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-open-analysis {
          background: rgba(99, 102, 241, 0.3);
          border: 1px solid rgba(99, 102, 241, 0.5);
          color: #e0e9ff;
        }

        .btn-open-analysis:hover {
          background: rgba(99, 102, 241, 0.4);
        }

        .btn-create-more {
          background: rgba(34, 197, 94, 0.3);
          border: 1px solid rgba(34, 197, 94, 0.5);
          color: #e0e9ff;
        }

        .btn-create-more:hover {
          background: rgba(34, 197, 94, 0.4);
        }

        .generated-list {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          min-height: 0;
        }

        .generated-item {
          background: rgba(20, 17, 48, 0.6);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }

        .item-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(99, 102, 241, 0.1);
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
        }

        .item-icon {
          font-size: 1.25rem;
        }

        .item-title {
          color: #e0e9ff;
          font-weight: 600;
        }

        .item-preview {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          min-height: 0;
        }
      `}</style>
    </div>
  );
}

export default PackageDesign;
